import {
  Link as CarbonLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Launch } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { PageHeader } from '../components/PageHeader';
import { StatusTag } from '../components/StatusTag';
import { ordersKey, useFormat, useOrdersApi, type OrdersMode } from './model';
import { Facts, Timeline, TransactionFrame } from './TransactionDetail';
import { StoreName } from './TransactionFilters';
import './orders.scss';

const OWN_DRIVER = 'own_driver';

/** One delivery: provider or own driver, where it went, and every status change (ADR 0009). */
export function ShippingHistoryPage({ mode }: { mode: OrdersMode }) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const detail = useQuery({ queryKey: [...ordersKey, 'delivery', id], queryFn: () => api.delivery(id) });
  const data = detail.data;
  const d = data?.delivery;
  const status = (s: string) => t(`orders.delivery.statuses.${s}` as I18nKey, { defaultValue: s });
  const provider = (method: string, name: string | null, code: string | null) =>
    method === OWN_DRIVER ? t('transactions.filters.ownDriver') : (name ?? code ?? '');
  const address = (data?.address ?? {}) as Record<string, string | null>;

  return (
    <TransactionFrame error={detail.error} loading={!data} backTo="/sales/shipping">
      {data && d && (
        <>
          <PageHeader
            title={t('transactions.detail.shippingTitle', { number: d.orderNumber })}
            tag={<StatusTag status={d.status}>{status(d.status)}</StatusTag>}
          />
          <Facts
            facts={[
              [t('transactions.detail.facts.order'), <Link key="order" to={`/orders/${d.orderId}`}>{d.orderNumber}</Link>],
              [t('transactions.detail.facts.store'), mode === 'ho' ? <StoreName key="store" id={d.storeId} /> : null],
              [t('transactions.detail.facts.provider'), provider(d.method, d.providerName, d.providerCode)],
              [t('transactions.detail.facts.reference'), d.providerReference],
              [
                t('transactions.detail.facts.tracking'),
                d.trackingUrl ? (
                  <CarbonLink key="track" href={d.trackingUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>
                    {t('orders.delivery.track')}
                  </CarbonLink>
                ) : null,
              ],
              [t('transactions.detail.facts.driver'), [d.driverName, d.driverPhone].filter(Boolean).join(' · ')],
              [t('transactions.detail.facts.customer'), [d.customerName, d.customerPhone].filter(Boolean).join(' · ')],
              [t('transactions.detail.facts.address'), [address.street, address.ward, address.province, address.note].filter(Boolean).join(', ')],
              [t('transactions.detail.facts.fee'), <span key="fee" className="pos-admin-page__number">{format.money(d.fee, d.currencyCode)}</span>],
              [t('transactions.detail.facts.createdAt'), format.dateTime(d.createdAt)],
              [t('transactions.detail.facts.pickedUpAt'), format.dateTime(d.pickedUpAt)],
              [t('transactions.detail.facts.deliveredAt'), format.dateTime(d.deliveredAt)],
              [t('transactions.detail.facts.failureReason'), d.failureReason],
            ]}
          />

          <h2 className="pos-orders__section-title">{t('transactions.detail.timeline')}</h2>
          <Timeline
            format={format.dateTime}
            entries={data.events.map((e) => ({ id: e.id, at: e.occurredAt, title: status(e.status), detail: e.note ?? undefined, by: e.userName }))}
          />

          {data.otherAttempts.length > 0 && (
            <>
              <h2 className="pos-orders__section-title">{t('transactions.detail.otherAttempts')}</h2>
              <TableContainer>
              <Table aria-label={t('transactions.detail.otherAttempts')}>
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('transactions.detail.facts.createdAt')}</TableHeader>
                    <TableHeader>{t('transactions.detail.facts.provider')}</TableHeader>
                    <TableHeader>{t('transactions.detail.facts.reference')}</TableHeader>
                    <TableHeader>{t('transactions.detail.facts.status')}</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.otherAttempts.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>
                        <Link to={`/sales/shipping/${o.id}`}>{format.dateTime(o.createdAt)}</Link>
                      </TableCell>
                      <TableCell>{provider(o.method, o.providerName, o.providerCode)}</TableCell>
                      <TableCell>{o.providerReference ?? ''}</TableCell>
                      <TableCell>{status(o.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </TransactionFrame>
  );
}
